import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { AI_TIERS, getTierById } from "@/constants/aiTiersCatalog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const ChangeTierModal = ({ client, open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(String(client?.tier ?? "1"));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [billingLink, setBillingLink] = useState(false);

  useEffect(() => {
    if (open && client) {
      setSelectedTier(String(client.tier));
      setSubmitError("");
      setBillingLink(false);
    }
  }, [open, client]);

  if (!open || !client) return null;

  const currentTier = Number(client.tier);
  const selectedTierNum = Number(selectedTier);
  const currentTierInfo = getTierById(currentTier);
  const selectedTierInfo = getTierById(selectedTierNum);
  const isDowngrade = selectedTierNum < currentTier;
  const isNoop = selectedTierNum === currentTier;
  const usageExceedsDowngrade =
    isDowngrade &&
    selectedTierInfo &&
    (client.creditsUsed ?? 0) > selectedTierInfo.credits;

  const getAuthHeader = () => getPartnerAuthHeaders();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNoop || usageExceedsDowngrade || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError("");
    setBillingLink(false);

    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/partner/clients/${client.id}/tier`,
        { tier: selectedTierNum },
        { headers: getAuthHeader() }
      );
      toast.success(`Tier updated to Tier ${selectedTierNum}`);
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      const message = extractErrorMessage(err, "Tier change failed. Please try again.");
      setSubmitError(message);
      if (err?.response?.status === 402) {
        setBillingLink(true);
        toast.error(message);
      } else if (err?.response?.status === 409) {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      data-testid="change-tier-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-partner bg-[#11151F] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-partner">
          <div>
            <h2 className="text-lg font-bold text-partner">Change tier</h2>
            <p className="text-sm text-partner-muted font-body mt-1">
              {client.name} · Tier {currentTier}
              {currentTierInfo ? ` ($${currentTierInfo.wholesale}/mo wholesale)` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-partner-muted hover:text-partner hover:bg-white/5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <div className="grid gap-3">
            {AI_TIERS.map((tier) => {
              const isCurrent = tier.id === currentTier;
              const isSelected = selectedTier === String(tier.id);
              return (
                <label
                  key={tier.id}
                  className={`flex flex-col gap-1 px-4 py-3 rounded-lg border transition-all ${
                    isCurrent
                      ? "border-partner bg-white/5 opacity-60 cursor-not-allowed"
                      : isSelected
                        ? "border-[#00D2FF]/40 bg-[#00D2FF]/10 text-partner-accent cursor-pointer"
                        : "border-partner text-partner-muted hover:border-[#00D2FF]/20 cursor-pointer"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="changeTier"
                      value={String(tier.id)}
                      checked={isSelected}
                      disabled={isCurrent}
                      onChange={(e) => {
                        setSelectedTier(e.target.value);
                        setSubmitError("");
                      }}
                      className="accent-[#00D2FF]"
                    />
                    <span className="text-sm font-body font-semibold">
                      {tier.deployLabel}
                      {isCurrent ? " (current)" : ""}
                    </span>
                  </span>
                  <span className="text-xs font-body text-partner-muted pl-6">
                    {tier.credits.toLocaleString()} AI Credits included
                  </span>
                </label>
              );
            })}
          </div>

          {!isNoop && selectedTierInfo && currentTierInfo && (
            <p className="text-xs text-partner-muted font-body">
              Wholesale: ${currentTierInfo.wholesale}/mo → ${selectedTierInfo.wholesale}/mo. Proration
              applies for the current billing cycle via Stripe.
            </p>
          )}

          {usageExceedsDowngrade && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-100/90 font-body">
                Cannot downgrade: this client has used {client.creditsUsed} credits, but Tier{" "}
                {selectedTierNum} includes only {selectedTierInfo.credits} credits this period.
              </p>
            </div>
          )}

          {submitError && (
            <div className="space-y-2">
              <p className="text-sm text-red-400 font-body" data-testid="change-tier-error">
                {submitError}
              </p>
              {billingLink && (
                <Link
                  to="/partner/billing"
                  className="text-sm font-semibold text-partner-accent hover:underline"
                >
                  Go to Partner Billing
                </Link>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:bg-[#080A0F] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isNoop || usageExceedsDowngrade}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="change-tier-submit"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? "Updating…" : "Confirm tier change"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangeTierModal;
